import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, CalendarClock, MessageSquare, Phone, Video, X, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fetchBookingAlerts, startConsultation } from "@lawyer/services/lawyerApi";
import { playBookingChime } from "@lawyer/lib/bookingChime";
import { useLawyerSocket } from "@lawyer/hooks/useLawyerSocket";
import { LAWYER_BASE } from "@lawyer/constants/route";

export const POLL_MS = 10000;
const MODE_ICON = { chat: MessageSquare, audio: Phone, video: Video };
const sinceKey = (lid) => `advoset.lawyer.alerts.since.${lid}`;

// Mounted once at the lawyer-app root so a paid booking pops a card (instant
// bookings first) + soft chime on every portal screen, including live rooms.
// Real-time via /ws/lawyer; /lawyer/alerts polling is the fallback for any
// gap. Last-seen time is kept per lawyer so a refresh never re-alerts.
export function NewBookingAlert({ lawyerId, enabled, onSeen, onAvailabilityChanged }) {
  const [queue, setQueue] = useState([]);
  const [starting, setStarting] = useState(false);
  const navigate = useNavigate();
  const sinceRef = useRef(null);
  const seenRef = useRef(new Set());
  const pollRef = useRef(null);

  const enqueue = useCallback((items) => {
    const fresh = items.filter((f) => !seenRef.current.has(f.id));
    if (!fresh.length) return;
    fresh.forEach((f) => seenRef.current.add(f.id));
    fresh.sort((a, b) => (b.booking_type === "instant") - (a.booking_type === "instant"));
    setQueue((q) => [...q, ...fresh]);
    playBookingChime();
    onSeen?.();
  }, [onSeen]);

  useEffect(() => {
    if (!lawyerId || !enabled) return undefined;
    const key = sinceKey(lawyerId);
    if (!localStorage.getItem(key)) localStorage.setItem(key, new Date().toISOString());
    sinceRef.current = localStorage.getItem(key);
    let stopped = false;
    const tick = async () => {
      if (stopped || document.hidden) return;
      try {
        const res = await fetchBookingAlerts(sinceRef.current);
        if (stopped) return;
        sinceRef.current = res.now;
        localStorage.setItem(key, res.now);
        if (res.items?.length) enqueue(res.items);
      } catch { /* transient — next tick retries */ }
    };
    pollRef.current = tick;
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { stopped = true; pollRef.current = null; clearInterval(id); };
  }, [lawyerId, enabled, enqueue]);

  const onEvent = useCallback((evt) => {
    if (evt?.type === "new_booking" && evt.item?.id) enqueue([evt.item]);
    if (evt?.type === "booking_rescheduled" && evt.item?.id) {
      const it = evt.item;
      playBookingChime();
      toast.info(`${it.user_name || "A client"} moved their ${it.consultation_type || "chat"} consultation to ${it.slot_date} · ${it.slot_time} IST`, {
        description: it.old_slot_date ? `Previously ${it.old_slot_date} · ${it.old_slot_time}` : undefined,
        duration: 12000,
        action: { label: "Open", onClick: () => navigate(`${LAWYER_BASE}/consultations/${it.id}/room`) },
      });
      onSeen?.();
    }
    if (evt?.type === "lawyer_no_show" && evt.item?.id) {
      const it = evt.item;
      toast.error("Consultation missed — closed as lawyer no-show", {
        description: `You did not join the ${it.consultation_type || ""} consultation with ${it.user_name || "the client"} (${it.slot_date} · ${it.slot_time} IST) within the allowed waiting period.`,
        duration: 15000,
        action: { label: "View", onClick: () => navigate(`${LAWYER_BASE}/consultations/${it.id}/room`) },
      });
      onSeen?.();
    }
    // Completion changes availability, but does not require a full dashboard reload.
    if (evt?.type === "consultation_completed") onAvailabilityChanged?.();
  }, [enqueue, navigate, onSeen, onAvailabilityChanged]);
  const onReconnect = useCallback(() => {
    pollRef.current?.();
    onAvailabilityChanged?.();
  }, [onAvailabilityChanged]);
  useLawyerSocket({ enabled: !!lawyerId && !!enabled, onEvent, onReconnect });

  const current = queue[0];
  if (!current) return null;
  const instant = current.booking_type === "instant";
  const Mode = MODE_ICON[current.consultation_type] || MessageSquare;
  const dismiss = () => setQueue((q) => q.slice(1));
  const roomPath = `${LAWYER_BASE}/consultations/${current.id}/room`;
  const open = () => { dismiss(); navigate(roomPath); };
  // Start = mark live (client is told the lawyer joined) and land in the room in one tap.
  const startNow = async () => {
    setStarting(true);
    try {
      await startConsultation(current.id);
      toast.success("Consultation started — the client has been notified");
      dismiss();
      navigate(roomPath);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start — opening the room instead");
      open();
    } finally { setStarting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" data-testid="new-booking-alert" role="alertdialog" aria-live="assertive">
      <div className="max-w-md w-full bg-white rounded-3xl p-6 shadow-2xl border-4 border-[#D4AF37] animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`h-12 w-12 rounded-2xl grid place-items-center ${instant ? "bg-[#D4AF37] text-[#0A2540]" : "bg-[#0A2540] text-[#D4AF37]"}`}>
              {instant ? <Zap size={22} /> : <CalendarClock size={22} />}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">New booking · paid</p>
              <p className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }} data-testid="new-booking-title">
                {instant ? "Instant consultation — client is waiting" : "New consultation booked"}
              </p>
            </div>
          </div>
          <button onClick={dismiss} aria-label="Dismiss" data-testid="new-booking-dismiss" className="text-slate-400 hover:text-[#0A2540]"><X size={18} /></button>
        </div>
        <div className="mt-4 rounded-2xl bg-[#FBF4DF] p-4 text-sm text-[#0A2540]" data-testid="new-booking-details">
          <p><strong>{current.user_name || "A client"}</strong> · <Mode size={13} className="inline -mt-0.5" /> {current.consultation_type}{current.duration_minutes ? ` · ${current.duration_minutes} min` : ""}</p>
          {!instant && current.slot_date && <p className="mt-1 text-slate-600">{current.slot_date} at {current.slot_time}</p>}
          {(() => {
            const earningPaise = current.lawyer_fee_post_discount_paise ?? current.lawyer_payout_paise ?? current.lawyer_fee_paise;
            return earningPaise != null ? <p className="mt-1 text-slate-600">Your fee: <strong>₹{Math.round(earningPaise / 100)}</strong></p> : null;
          })()}
          {current.documents_count > 0 && <p className="mt-1 text-slate-600" data-testid="new-booking-docs">{current.documents_count} document{current.documents_count > 1 ? "s" : ""} attached — open the room to read them</p>}
          {current.query_preview && <p className="mt-2 text-xs text-slate-600 italic">“{current.query_preview}{current.query_preview.length >= 120 ? "…" : ""}”</p>}
        </div>
        {queue.length > 1 && <p className="mt-2 text-xs text-slate-500" data-testid="new-booking-more">+{queue.length - 1} more new booking{queue.length > 2 ? "s" : ""}</p>}
        <div className="mt-5 flex flex-wrap gap-2 justify-end">
          <Button variant="outline" className="rounded-xl" onClick={dismiss} disabled={starting} data-testid="new-booking-later">Later</Button>
          <Button variant="outline" className="rounded-xl" onClick={open} disabled={starting} data-testid="new-booking-open">{instant ? "Open room" : "View consultation"}</Button>
          {instant && (
            <Button className="adv-btn-primary rounded-xl" onClick={startNow} disabled={starting} data-testid="new-booking-start">
              {starting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Play size={14} className="mr-1" />} Start consultation
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
